/**
 * CallbacksCard — scheduled call times for one contact, on the detail right
 * rail. Three jobs:
 *   1. Show the upcoming booked callback prominently (with complete / no-show /
 *      cancel one-tap outcomes) and past callbacks behind a toggle.
 *   2. Book one as a recruiter ("they asked me to call Tuesday at 3") via an
 *      inline form with quick picks — any future time, unlike the patient grid.
 *   3. Send the patient a "pick a time" booking link by SMS (the Trialfacts
 *      flow): POST /contacts/:id/booking-link { send:true } — same compliance
 *      gate + test-mode override as the composer.
 * Also surfaces the contact's cadence state ("Attempt 2 of 6 · next follow-up
 * due in 3h") so a recruiter on the detail page sees where outreach stands.
 * While the lead is on an outreach list that line is the LIST's schedule
 * instead ("On Neuropathy re-engagement · try 2 of 3 · next try Tue 10:00"):
 * the cadence engine skips list leads (docs/crm-outreach-lists-plan.md §B8), so
 * its counter would describe attempts nobody is going to make.
 *
 * Out-of-office coverage (docs/crm-out-of-office-coverage-plan.md): a booked
 * callback keeps its `assignee_user_id` while that recruiter is away — the
 * viewer is only covering the call. Rows the server tags with `coverage` wear a
 * <CoverageBadge>, so "am I the one making this call?" has a visible answer on
 * the card where the call gets marked complete.
 *
 * Overbooking check + slot picker (callbackSlots/, see crm/CLAUDE.md): once a
 * full date and time is chosen in the book or edit form, the recruiter's
 * calendar for that day is read and an overlap shows an amber alert under the
 * time fields with a one-press Override (no note); "Find a time" opens a week
 * grid of the recruiter's calendar to place the call by eye. The server checks
 * again on the write (409 overbooked -> confirm -> retry with allow_overbook).
 */
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CalendarClock, CalendarSearch, Check, Link2, Loader2, Pencil, PhoneMissed, Plus, X } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { Contact, ScheduledCallback } from '../types';
import { notifyStatsChanged, relTime, useTeam } from '../lib';
import CoverageBadge, { coverageOf } from './CoverageBadge';
import { outTodaySuffix } from './OwnerSelect';
import { useConfirm } from './ui';
import { useLeadOutreach } from '../outreach/LeadOutreachChip';
import { listCadenceLine } from '../outreach/outreachSurfaces';
import { callbackEditNotice, callbackOrigin, offersOwnerMove as offersOwnerMoveFor } from './scheduler/schedulerLogic';
import CallbackSlotPicker, { SlotPick } from './callbackSlots/CallbackSlotPicker';
import SlotConflictAlert from './callbackSlots/SlotConflictAlert';
import { useSlotCheck } from './callbackSlots/useSlotCheck';
import { Conflict, conflictHeadline, conflictLine, sendWithOverrides } from './callbackSlots/slotLogic';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** A Date as a datetime-local input value (local clock, minute precision). */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Tenant booking window, as served by GET /settings/booking. */
interface BookingHours {
  days: number[];
  day_start: string;
  day_end: string;
}
const FALLBACK_HOURS: BookingHours = { days: [1, 2, 3, 4, 5], day_start: '08:00', day_end: '17:00' };

const minutesOf = (hhmm: string): number => {
  const [h, m] = hhmm.split(':').map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : 0;
};

/**
 * The next moment at/after `from` that lands inside the booking window, aiming
 * for `preferMinutes` past midnight. Walks forward a day at a time to the next
 * OPEN day — so on a Friday the quick picks offer Monday, not a weekend the
 * server would reject.
 */
function nextOpenSlot(hours: BookingHours, from: Date, preferMinutes: number): Date {
  const open = hours.days.length ? hours.days : FALLBACK_HOURS.days;
  const startMin = minutesOf(hours.day_start);
  const endMin = minutesOf(hours.day_end);
  const target = Math.min(Math.max(preferMinutes, startMin), Math.max(startMin, endMin - 30));
  const d = new Date(from);
  for (let i = 0; i < 14; i++) {
    if (open.includes(d.getDay())) {
      const candidate = new Date(d);
      candidate.setHours(Math.floor(target / 60), target % 60, 0, 0);
      if (candidate.getTime() > from.getTime()) return candidate;
    }
    d.setDate(d.getDate() + 1);
    d.setHours(0, 0, 0, 0);
  }
  return from;
}

/** "Today", "Tomorrow", else "Mon" — for a quick-pick label. */
function dayWord(d: Date): string {
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const delta = Math.round((midnight(d) - midnight(new Date())) / 86400e3);
  if (delta === 0) return 'Today';
  if (delta === 1) return 'Tomorrow';
  return d.toLocaleDateString(undefined, { weekday: 'short' });
}

const shortTime = (d: Date) =>
  d.toLocaleTimeString(undefined, { hour: 'numeric', minute: d.getMinutes() ? '2-digit' : undefined }).replace(' ', '');

/** Three picks that always land inside the booking window (labels follow). */
function quickSlots(hours: BookingHours): Array<{ label: string; value: string }> {
  const soon = new Date(Date.now() + 2 * 3600e3);
  soon.setMinutes(0, 0, 0);
  const picks = [
    nextOpenSlot(hours, new Date(), soon.getHours() * 60),
    nextOpenSlot(hours, new Date(new Date().setHours(23, 59, 59, 999)), 9 * 60),
    nextOpenSlot(hours, new Date(new Date().setHours(23, 59, 59, 999)), 15 * 60),
  ];
  return picks.map((d) => ({ label: `${dayWord(d)} ${shortTime(d)}`, value: toLocalInput(d) }));
}

/** "Mon, Jun 15 at 3:00 PM" in the viewer's locale. */
function slotLabel(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} at ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}

/** Whole hours, 1–12 — the native picker's hour segment, unchanged. */
const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1));

/** Minutes on a 15-minute grid — the one segment that needed shrinking from
 *  60 choices down to 4. Native datetime-local minute spinners ignore `step`
 *  in Chrome (it only affects arrow-key increments, not what's listed), so a
 *  real dropdown is the only way to actually limit the choices. */
const MINUTE_OPTIONS = ['00', '15', '30', '45'];

/** Date input + hour / minute / AM-PM dropdowns that together stand in for a
 *  single datetime-local input — same segments as the native picker (hour,
 *  minute, AM/PM), just with the minute segment capped at :00/:15/:30/:45.
 *  Keeps the "YYYY-MM-DDTHH:MM" string contract so callers (book/edit forms,
 *  quick picks) don't need to change. Runs its own draft state so a
 *  half-built selection (hour picked, minute not yet) survives — the
 *  parent's `value` only updates once all four pieces are chosen, and
 *  re-syncs from `value` when it changes externally (a quick-pick button, or
 *  the edit form opening with an existing time). A plain <select> also fixes
 *  the other half of the original complaint for free — choosing an option
 *  closes the dropdown immediately, no native picker to click away from. */
function DateTimeFields({
  value,
  onChange,
  className,
  autoFocus,
  ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  className: string;
  autoFocus?: boolean;
  ariaLabel: string;
}) {
  const parse = (v: string) => {
    if (!v) return { date: '', hour12: '', minute: '', ampm: '' };
    const [date, time] = v.split('T');
    const [h, m] = time.split(':').map(Number);
    return {
      date,
      hour12: String(h % 12 === 0 ? 12 : h % 12),
      minute: String(m).padStart(2, '0'),
      ampm: h >= 12 ? 'PM' : 'AM',
    };
  };

  const [draft, setDraft] = useState(() => parse(value));
  useEffect(() => { setDraft(parse(value)); }, [value]);

  const commit = (next: typeof draft) => {
    setDraft(next);
    if (next.date && next.hour12 && next.minute && next.ampm) {
      let h = Number(next.hour12) % 12;
      if (next.ampm === 'PM') h += 12;
      onChange(`${next.date}T${String(h).padStart(2, '0')}:${next.minute}`);
    }
  };

  return (
    <div className="space-y-1.5">
      <input
        type="date"
        value={draft.date}
        onChange={(e) => commit({ ...draft, date: e.target.value })}
        aria-label={`${ariaLabel} — date`}
        autoFocus={autoFocus}
        className={`${className} w-full`}
      />
      <div className="flex gap-1.5">
        <select
          value={draft.hour12}
          onChange={(e) => commit({ ...draft, hour12: e.target.value })}
          aria-label={`${ariaLabel} — hour`}
          className={`${className} min-w-0 flex-1 cursor-pointer`}
        >
          <option value="" disabled>Hour</option>
          {HOUR_OPTIONS.map((h) => (
            <option key={h} value={h}>{h}</option>
          ))}
        </select>
        <select
          value={draft.minute}
          onChange={(e) => commit({ ...draft, minute: e.target.value })}
          aria-label={`${ariaLabel} — minute`}
          className={`${className} min-w-0 flex-1 cursor-pointer`}
        >
          <option value="" disabled>Min</option>
          {MINUTE_OPTIONS.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <select
          value={draft.ampm}
          onChange={(e) => commit({ ...draft, ampm: e.target.value })}
          aria-label={`${ariaLabel} — AM/PM`}
          className={`${className} min-w-0 flex-1 cursor-pointer`}
        >
          <option value="" disabled>AM/PM</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
      </div>
    </div>
  );
}

const isBooked = (cb: ScheduledCallback) => cb.status === 'booked';

interface Props {
  contact: Contact;
  /** Tenant cadence ceiling (GET /contacts/:id max_attempts; defaults to 6). */
  maxAttempts?: number;
  /** Bump to refetch (e.g. after the timeline reloads). */
  reloadToken?: number;
  /** Fired after the card itself sends an SMS (booking link) so the parent can
   * reload the timeline + contact cadence state. */
  onSent?: () => void;
  /** Drop the rendered "pick a time" SMS into the docked composer (editable). */
  prefillComposer?: (text: string) => void;
  /** Rendered inside a rail disclosure row, which owns the chrome: drop this
   *  card's own border/background/shadow. Absent = the standalone card, byte
   *  for byte what the lead page renders today. */
  frameless?: boolean;
}

export default function CallbacksCard({ contact, maxAttempts = 6, reloadToken = 0, prefillComposer, frameless = false }: Props) {
  const contactId = contact.id;
  const confirm = useConfirm();
  // The same shared read as the header chip (one request per lead page), and
  // only for a lead whose payload says it is on a list.
  const { member: listMember } = useLeadOutreach(
    contactId,
    (contact as Contact & { active_outreach_list_id?: string | null }).active_outreach_list_id,
  );
  const { user } = useAuth();
  const { team, byId: teamById } = useTeam();
  const [callbacks, setCallbacks] = useState<ScheduledCallback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [showPast, setShowPast] = useState(false);

  // Inline book form
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  // Who takes the call. Defaults to the lead's owner, else the viewer: the
  // same person the server picks when no assignee is sent.
  const [bookAssignee, setBookAssignee] = useState('');

  // Inline edit form (reschedule time and/or reassign recruiter on a booked callback)
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editWhen, setEditWhen] = useState('');
  const [editAssignee, setEditAssignee] = useState('');
  const [editOriginal, setEditOriginal] = useState<{ scheduled_at: string; assignee_user_id: string | null } | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  // Scheduler mode (plan B5 LATER EDITS): handing a call the assistant booked
  // to someone else also offers to hand them the LEAD (ticked by default).
  const [editMoveOwner, setEditMoveOwner] = useState(true);

  // Booking-link send
  const [sendingLink, setSendingLink] = useState(false);

  // "Find a time": which form the slot picker is filling.
  const [pickerFor, setPickerFor] = useState<'book' | 'edit' | null>(null);

  // Tenant booking window — drives the quick picks and the hint under the form
  // so a recruiter isn't guessing at hours the server will reject.
  const [hours, setHours] = useState<BookingHours>(FALLBACK_HOURS);
  const [hoursLabel, setHoursLabel] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/callbacks`);
      setCallbacks(res.data?.callbacks || []);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(); }, [load, reloadToken]);

  // Non-fatal: a failed read just leaves the platform-default window in place.
  useEffect(() => {
    let alive = true;
    api.get('/api/crm/settings/booking')
      .then((res) => {
        if (!alive) return;
        const b = res.data?.booking;
        if (b?.days && b?.day_start && b?.day_end) {
          setHours({ days: b.days, day_start: b.day_start, day_end: b.day_end });
        }
        setHoursLabel(res.data?.hours_label || null);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const defaultAssignee = contact.owner_user_id || user?.id || '';

  // Live overlap check on the book form: the tenant's slot length, the chosen recruiter.
  const bookCheck = useSlotCheck({ enabled: formOpen, assignee: bookAssignee, value: when });

  // ...and on the edit form, only once the time or recruiter actually moved
  // (an untouched call is not re-judged), with the call itself left out.
  const editingCb = editingId ? callbacks.find((c) => c.id === editingId) : undefined;
  const editChanged = !!editOriginal && (
    editWhen !== toLocalInput(new Date(editOriginal.scheduled_at))
    || (editAssignee || null) !== editOriginal.assignee_user_id
  );
  const editCheck = useSlotCheck({
    enabled: !!editingId && editChanged,
    assignee: editAssignee,
    value: editWhen,
    durationMin: editingCb?.duration_min || undefined,
    excludeId: editingId,
  });

  /** Display name + "is it me?" for a recruiter id (the alert's and the confirm's headline). */
  const whoOf = (id: string | null | undefined) => ({
    name: id ? teamById.get(id)?.name || null : null,
    isMe: !!id && id === user?.id,
  });

  /** The server still refused an overlap (a booking landed since the live check,
   *  or the check never ran): list what is there and offer the override once. */
  const confirmOverbook = (conflicts: Conflict[], assignee: string | null, confirmLabel: string) => {
    const who = whoOf(assignee);
    return confirm({
      title: 'That time is already booked',
      message: (
        <div className="space-y-1.5">
          <p>{conflictHeadline(who.name, who.isMe)}</p>
          {conflicts.length > 0 && (
            <ul className="list-disc space-y-0.5 pl-5">
              {conflicts.map((c, i) => <li key={i}>{conflictLine(c)}</li>)}
            </ul>
          )}
          <p>Override to book it anyway.</p>
        </div>
      ),
      confirmLabel,
    });
  };

  /** A time (and recruiter) chosen in the slot picker lands in the open form. */
  const applyPick = (pick: SlotPick) => {
    if (pickerFor === 'book') {
      setWhen(pick.value);
      setBookAssignee(pick.assignee);
      if (pick.overridden) bookCheck.presetOverride(pick.assignee, pick.value);
    } else if (pickerFor === 'edit') {
      setEditWhen(pick.value);
      setEditAssignee(pick.assignee);
      if (pick.overridden) editCheck.presetOverride(pick.assignee, pick.value);
    }
  };

  const setBusy = (id: string, busy: boolean) =>
    setBusyIds((prev) => {
      const n = new Set(prev);
      if (busy) n.add(id); else n.delete(id);
      return n;
    });

  const setStatus = async (cb: ScheduledCallback, status: string) => {
    if (busyIds.has(cb.id)) return;
    setActionError(null);
    setBusy(cb.id, true);
    setCallbacks((prev) => prev.map((c) => (c.id === cb.id ? { ...c, status } : c)));
    try {
      await api.patch(`/api/crm/callbacks/${cb.id}`, { status });
      notifyStatsChanged(); // worklist buckets + the sidebar Today badge moved
    } catch (e: any) {
      setCallbacks((prev) => prev.map((c) => (c.id === cb.id ? { ...c, status: cb.status } : c)));
      setActionError(errMsg(e));
    } finally {
      setBusy(cb.id, false);
    }
  };

  /** Book, retrying with an explicit override for each refusal the server
   *  answers: outside business hours (422) and an overlap on the recruiter's
   *  calendar (409 overbooked), in either order, each confirmed once. The
   *  confirm is the ONLY way past either rule — a plain click can never
   *  schedule a call the office won't staff. An Override pressed on the live
   *  alert goes out as allow_overbook up front (no second question). */
  const book = async (e: FormEvent) => {
    e.preventDefault();
    if (!when || saving || bookCheck.blocking) return;
    setSaving(true);
    setActionError(null);
    const iso = new Date(when).toISOString();
    const assignee = bookAssignee || null;
    try {
      const sent = await sendWithOverrides(
        (f) => api.post(`/api/crm/contacts/${contactId}/callbacks`, {
          scheduled_at: iso,
          note: note.trim() || null,
          ...(assignee ? { assignee_user_id: assignee } : {}),
          ...(f.outsideHours ? { allow_outside_hours: true } : {}),
          ...(f.overbook ? { allow_overbook: true } : {}),
        }),
        {
          initial: { overbook: bookCheck.overridden },
          confirmOutsideHours: (hoursLabel) => confirm({
            title: `${slotLabel(iso)} is outside business hours (${hoursLabel || 'business hours'}).`,
            message: 'Book it anyway? The exception is recorded on the lead’s timeline.',
            confirmLabel: 'Book anyway',
          }),
          confirmOverbook: (conflicts) => confirmOverbook(conflicts, assignee, 'Override and book'),
        },
      );
      if (!sent) return;
      const res = sent.result;
      const cb: ScheduledCallback | undefined = res.data?.callback;
      if (cb) setCallbacks((prev) => [cb, ...prev].sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at)));
      notifyStatsChanged(); // a booked callback reshuffles the worklist buckets
      setWhen('');
      setNote('');
      setFormOpen(false);
      setPickerFor(null);
    } catch (e2: any) {
      setActionError(errMsg(e2));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (cb: ScheduledCallback) => {
    setEditingId(cb.id);
    setEditWhen(toLocalInput(new Date(cb.scheduled_at)));
    setEditAssignee(cb.assignee_user_id || '');
    setEditOriginal({ scheduled_at: cb.scheduled_at, assignee_user_id: cb.assignee_user_id });
    setEditMoveOwner(true);
    setActionError(null);
  };

  /** Offer "Also make <new recruiter> the lead's owner"? (schedulerLogic.offersOwnerMove) */
  const offersOwnerMove = (cb: ScheduledCallback) => offersOwnerMoveFor(cb, editAssignee, contact.owner_user_id);

  const cancelEdit = () => {
    setEditingId(null);
    setEditOriginal(null);
    if (pickerFor === 'edit') setPickerFor(null);
  };

  /** Save only the fields that actually changed — sending an unchanged
   *  scheduled_at would make the server treat it as a reschedule (resetting
   *  the SMS reminder ladder and re-checking business hours for no reason). */
  const saveEdit = async (cb: ScheduledCallback) => {
    if (!editWhen || editSaving || !editOriginal || editCheck.blocking) return;
    const newIso = new Date(editWhen).toISOString();
    const newAssignee = editAssignee || null;
    const body: Record<string, any> = {};
    if (newIso !== editOriginal.scheduled_at) body.scheduled_at = newIso;
    if (newAssignee !== editOriginal.assignee_user_id) body.assignee_user_id = newAssignee;
    if (Object.keys(body).length === 0) { cancelEdit(); return; }
    // Only ever for a flagged scheduler booking handed to a person (see
    // offersOwnerMove); the server re-checks every condition.
    if (body.assignee_user_id && offersOwnerMove(cb) && editMoveOwner) body.move_owner = true;
    // The overlap check is opt-in on PATCH (status taps never ask for it); a
    // new time or a new recruiter does.
    body.check_conflicts = true;
    setEditSaving(true);
    setActionError(null);
    try {
      const sent = await sendWithOverrides(
        (f) => api.patch(`/api/crm/callbacks/${cb.id}`, {
          ...body,
          ...(f.outsideHours ? { allow_outside_hours: true } : {}),
          ...(f.overbook ? { allow_overbook: true } : {}),
        }),
        {
          initial: { overbook: editCheck.overridden },
          // Only a new time can be outside hours; a recruiter-only change that
          // draws a 422 is shown as the error it is.
          confirmOutsideHours: body.scheduled_at
            ? (hoursLabel) => confirm({
              title: `${slotLabel(newIso)} is outside business hours (${hoursLabel || 'business hours'}).`,
              message: 'Save it anyway? The exception is recorded on the lead’s timeline.',
              confirmLabel: 'Save anyway',
            })
            : undefined,
          confirmOverbook: (conflicts) => confirmOverbook(conflicts, newAssignee, 'Override and save'),
        },
      );
      if (!sent) return;
      const res = sent.result;
      const updated: ScheduledCallback | undefined = res.data?.callback;
      setCallbacks((prev) => {
        const next = updated ? prev.map((c) => (c.id === cb.id ? { ...c, ...updated } : c)) : prev;
        return next.slice().sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at));
      });
      notifyStatsChanged();
      cancelEdit();
      // Scheduler bookings answer with calendar warnings (never a refusal)
      // and, when asked, whether the lead moved with the call.
      const editNote = callbackEditNotice(
        body.move_owner === true,
        res.data,
        teamById.get(body.assignee_user_id)?.name || 'the new recruiter',
      );
      if (editNote) setNotice(editNote);
    } catch (e: any) {
      if (e?.response?.status === 409 && e?.response?.data?.code === 'callback_changed') {
        // Someone re-assigned this call a moment ago: show the call as it is now.
        cancelEdit();
        await load();
        setActionError('Someone else changed this call just now. It has been reloaded; make your change again.');
      } else {
        setActionError(errMsg(e));
      }
    } finally {
      setEditSaving(false);
    }
  };

  // Draft the rendered "pick a time" SMS into the docked composer instead of
  // direct-sending: the recruiter edits the wording and sends via the normal
  // gated /messages path (opt-out / consent / quiet-hours / char-limit + the
  // outreach-attempt accounting all happen server-side on that send). Minting
  // on { send:false } reuses the live token, so the previewed URL ships as-is.
  const sendLink = async () => {
    if (sendingLink) return;
    setSendingLink(true);
    setActionError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/contacts/${contactId}/booking-link`, { send: false });
      const body: string | undefined = res.data?.body;
      if (body && prefillComposer) {
        prefillComposer(body);
        setNotice('Draft added to the message box below — edit and send from there.');
      } else {
        setNotice(res.data?.url ? 'Booking link ready.' : 'Booking link created.');
      }
    } catch (e: any) {
      const hint = e?.response?.data?.hint;
      setActionError(hint ? `${errMsg(e)} — ${hint}` : errMsg(e));
    } finally {
      setSendingLink(false);
    }
  };

  const now = Date.now();
  const upcoming = callbacks
    .filter((cb) => isBooked(cb))
    .sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));
  const past = callbacks.filter((cb) => !isBooked(cb));

  // Cadence status line from the trigger-maintained contact columns. Numbering
  // matches the Today page: "Attempt N of M" names the attempt that is DUE
  // (attempt_count = attempts already made). With a booked callback, the
  // cadence is paused (next_action_at points at the slot), so say that instead.
  const attempts = contact.attempt_count ?? 0;
  const dueLabelText = contact.next_action_at
    ? (new Date(contact.next_action_at).getTime() <= now ? 'due now' : relTime(contact.next_action_at))
    : '';
  // A waiting reply still outranks everything; after that, a list lead reads
  // the list's own retry schedule (a booked callback shows up in it as its
  // next try), never the cadence counter.
  const cadenceLine = contact.needs_reply_at
    ? `Waiting on your reply (${relTime(contact.needs_reply_at)})`
    : listMember
      ? listCadenceLine(listMember)
      : upcoming.length > 0
        ? 'Outreach cadence paused — callback booked'
        : contact.next_action_at
          ? attempts === 0
            ? `First touch ${dueLabelText}`
            : `Attempt ${Math.min(attempts + 1, maxAttempts)} of ${maxAttempts} · next follow-up ${dueLabelText}`
          : attempts > 0
            ? `${attempts} contact attempt${attempts === 1 ? '' : 's'} made`
            : null;

  const inputCls =
    'w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';

  const findTimeCls =
    'inline-flex items-center gap-1 rounded-full border border-primary-300 px-2.5 py-1 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-700 dark:text-primary-400 dark:hover:bg-primary-900/20';

  const statusChip = (cb: ScheduledCallback) => {
    const map: Record<string, string> = {
      completed: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
      cancelled: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      no_show: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    };
    return map[cb.status] || 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300';
  };

  return (
    <div
      className={frameless
        ? ''
        : 'rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6'}
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Callbacks
          {!loading && upcoming.length > 0 && (
            <span className="text-sm font-normal text-gray-500 dark:text-gray-400">({upcoming.length})</span>
          )}
        </h3>
        <button
          onClick={() => {
            if (!formOpen) setBookAssignee(defaultAssignee);
            setFormOpen((s) => !s);
            setActionError(null);
          }}
          aria-expanded={formOpen}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-primary-600 px-3 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
        >
          {formOpen ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
          {formOpen ? 'Cancel' : 'Schedule'}
        </button>
      </div>

      {cadenceLine && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{cadenceLine}</p>
      )}

      {formOpen && (
        <form onSubmit={book} className="mb-3 space-y-2.5 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
          <div>
            <DateTimeFields
              value={when}
              onChange={setWhen}
              ariaLabel="Callback date and time"
              autoFocus
              className={inputCls}
            />
            <SlotConflictAlert
              conflicts={bookCheck.conflicts}
              overridden={bookCheck.overridden}
              {...whoOf(bookAssignee)}
              onOverride={bookCheck.override}
              onFindAnother={() => setPickerFor('book')}
            />
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {quickSlots(hours).map((q) => (
                <button
                  key={q.label}
                  type="button"
                  onClick={() => setWhen(q.value)}
                  className="rounded-full border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {q.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPickerFor('book')}
                className={findTimeCls}
              >
                <CalendarSearch className="h-3.5 w-3.5" aria-hidden="true" />
                Find a time
              </button>
            </div>
            {hoursLabel && (
              <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                Business hours: {hoursLabel}. Booking outside them asks you to confirm.
              </p>
            )}
          </div>
          <select
            value={bookAssignee}
            onChange={(e) => setBookAssignee(e.target.value)}
            aria-label="Recruiter"
            className={`${inputCls} cursor-pointer`}
          >
            {/* The default (the lead's owner) may have left the team list:
                keep it selectable rather than silently booking someone else. */}
            {bookAssignee && !teamById.has(bookAssignee) && (
              <option value={bookAssignee}>{bookAssignee === user?.id ? 'Me' : 'Lead owner'}</option>
            )}
            {team.map((m) => (
              <option key={m.id} value={m.id}>
                {m.id === user?.id ? `${m.name} (me)` : m.name}{outTodaySuffix(m)}
              </option>
            ))}
          </select>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note (optional) — e.g. prefers afternoons"
            aria-label="Callback note"
            maxLength={500}
            className={inputCls}
          />
          <button
            type="submit"
            disabled={!when || saving || bookCheck.blocking}
            title={bookCheck.blocking ? 'That time is already booked: override or pick another time' : undefined}
            className="inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900 sm:min-h-0"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
            {saving ? 'Booking…' : 'Book callback'}
          </button>
        </form>
      )}

      {notice && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {notice}
        </div>
      )}
      {actionError && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-primary-600" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
          <button onClick={load} className="ml-2 font-medium underline hover:no-underline">Retry</button>
        </div>
      ) : (
        <>
          {upcoming.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No callback scheduled. Book one, or text the patient a link to pick their own time.
            </p>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {upcoming.map((cb) => {
                const busy = busyIds.has(cb.id);
                const overdue = new Date(cb.scheduled_at).getTime() < now;
                const editing = editingId === cb.id;
                const assigneeName = cb.assignee_user_id ? teamById.get(cb.assignee_user_id)?.name : null;
                const origin = callbackOrigin(cb, 'card');
                const moveOffer = editing && offersOwnerMove(cb);
                return (
                  <div key={cb.id} className="py-2.5">
                    <div className="flex items-start gap-2.5">
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-medium ${overdue ? 'text-red-700 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
                          {slotLabel(cb.scheduled_at)}
                          {overdue && <span className="ml-1.5 text-xs font-normal">(overdue)</span>}
                          {cb.overbooked && (
                            <span
                              title="Booked on top of another appointment on the recruiter's calendar"
                              className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 align-middle text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                            >
                              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                              Overbooked
                            </span>
                          )}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                          {origin.warn
                            ? <span className="font-medium text-amber-700 dark:text-amber-400">{origin.label}</span>
                            : origin.label} · {relTime(cb.scheduled_at)}
                          {assigneeName ? ` · ${assigneeName}` : ''}
                          {cb.note ? ` · ${cb.note}` : ''}
                        </p>
                        {/* Booked to someone who is out — this call is yours to
                            make today, but the callback is still theirs. */}
                        {coverageOf(cb) && (
                          <div className="mt-1">
                            <CoverageBadge coverage={coverageOf(cb)} />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-1">
                        <button
                          onClick={() => (editing ? cancelEdit() : startEdit(cb))}
                          title={editing ? 'Cancel edit' : 'Edit time or recruiter'}
                          aria-label={editing ? 'Cancel edit' : 'Edit callback'}
                          aria-expanded={editing}
                          className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-primary-600 hover:text-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600"
                        >
                          {editing ? <X className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={() => setStatus(cb, 'completed')}
                          disabled={busy}
                          title="Mark completed"
                          aria-label="Mark callback completed"
                          className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-green-600 hover:text-green-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                        >
                          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                        </button>
                        <button
                          onClick={() => setStatus(cb, 'no_show')}
                          disabled={busy}
                          title="No-show"
                          aria-label="Mark callback as no-show"
                          className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-red-600 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600"
                        >
                          <PhoneMissed className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setStatus(cb, 'cancelled')}
                          disabled={busy}
                          title="Cancel callback"
                          aria-label="Cancel callback"
                          className="flex h-11 w-11 items-center justify-center rounded-full sm:h-8 sm:w-8 border border-gray-300 text-gray-400 transition-colors duration-150 hover:border-gray-500 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:hover:text-gray-300"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                    {editing && (
                      <div className="mt-2 space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-gray-700 dark:bg-gray-900/40">
                        <DateTimeFields
                          value={editWhen}
                          onChange={setEditWhen}
                          ariaLabel="Callback date and time"
                          className={inputCls}
                        />
                        <SlotConflictAlert
                          conflicts={editCheck.conflicts}
                          overridden={editCheck.overridden}
                          {...whoOf(editAssignee)}
                          onOverride={editCheck.override}
                          onFindAnother={() => setPickerFor('edit')}
                        />
                        <button
                          type="button"
                          onClick={() => setPickerFor('edit')}
                          className={findTimeCls}
                        >
                          <CalendarSearch className="h-3.5 w-3.5" aria-hidden="true" />
                          Find a time
                        </button>
                        <select
                          value={editAssignee}
                          onChange={(e) => setEditAssignee(e.target.value)}
                          aria-label="Recruiter"
                          className={`${inputCls} cursor-pointer`}
                        >
                          <option value="">Unassigned</option>
                          {/* Booking a call onto an empty chair is the same
                              mistake as assigning a lead to one, so the picker
                              carries the same hint. Still selectable: a call
                              booked for next week belongs to whoever owns it
                              then, not to whoever is at a desk today. */}
                          {team.map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.id === user?.id ? `${m.name} (me)` : m.name}{outTodaySuffix(m)}
                            </option>
                          ))}
                        </select>
                        {moveOffer && (
                          <label className="flex items-start gap-2 text-xs text-gray-700 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={editMoveOwner}
                              onChange={(e) => setEditMoveOwner(e.target.checked)}
                              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-600"
                            />
                            <span>
                              Also make {teamById.get(editAssignee)?.name || 'them'} the lead&apos;s owner
                              <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                                The scheduling assistant booked this call. Whoever takes it usually owns the lead.
                              </span>
                            </span>
                          </label>
                        )}
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => saveEdit(cb)}
                            disabled={!editWhen || editSaving || editCheck.blocking}
                            title={editCheck.blocking ? 'That time is already booked: override or pick another time' : undefined}
                            className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-gray-900 sm:min-h-0"
                          >
                            {editSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-400 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <button
            onClick={sendLink}
            disabled={sendingLink || !contact.phone_e164 || contact.sms_opt_out}
            title={
              contact.sms_opt_out
                ? 'Patient opted out of SMS'
                : !contact.phone_e164
                  ? 'No phone number on file'
                  : 'Text the patient a link to pick their own call time'
            }
            className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg border border-primary-600 px-3 py-2 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-primary-400 dark:hover:bg-primary-900/20 sm:min-h-0"
          >
            {sendingLink ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
            {sendingLink ? 'Drafting…' : 'Draft "pick a time" text'}
          </button>

          {past.length > 0 && (
            <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
              <button
                onClick={() => setShowPast((s) => !s)}
                aria-expanded={showPast}
                className="text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:text-gray-200"
              >
                {showPast ? 'Hide' : 'Show'} past ({past.length})
              </button>
              {showPast && (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {past.map((cb) => (
                    <div key={cb.id} className="flex items-center gap-2.5 py-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm text-gray-500 dark:text-gray-400">{slotLabel(cb.scheduled_at)}</p>
                      </div>
                      <span className={`inline-flex flex-shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusChip(cb)}`}>
                        {cb.status === 'no_show' ? 'no-show' : cb.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* "Find a time": the week grid for whichever form asked (one at a time). */}
      <CallbackSlotPicker
        isOpen={pickerFor !== null}
        onClose={() => setPickerFor(null)}
        value={pickerFor === 'edit' ? editWhen : when}
        assignee={pickerFor === 'edit' ? editAssignee : bookAssignee}
        durationMin={pickerFor === 'edit' ? editingCb?.duration_min || undefined : undefined}
        excludeCallbackId={pickerFor === 'edit' ? editingId : null}
        current={pickerFor === 'edit' && editingCb
          ? { start: editingCb.scheduled_at, durationMin: editingCb.duration_min || 30, assignee: editingCb.assignee_user_id }
          : null}
        studyId={contact.primary_study_id || null}
        onPick={applyPick}
      />
    </div>
  );
}
